import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses';
import {
  fieldCantBeEmptyMsg,
  invalidValueMsg,
  mainUserEmailMsg,
  mainUserNameMsg,
  mainUserPhoneMsg,
  t,
} from '../../../../common/i18n';
import { scrollTo } from '../../../../common/utils/browserUtils';
import { EMAIL_REGEX } from '../../../../common/utils/emailUtils';
import { PHONE_NUMBER_REGEX } from '../../../../common/utils/phoneNumberUtils';
import { Input } from '../../../Input/Input';
import type { PlaceOrderFormData } from '../../placeOrderTypes';

export interface YttAdditionalFieldsHandle {
  validate: () => boolean;
  scrollToFirstError: () => void;
  getErrorCount: () => number;
}

export interface YttAdditionalFieldsProps {
  globalIndex: number;
}

interface FieldErrors {
  mainUserName?: string;
  mainUserEmail?: string;
  mainUserPhone?: string;
}

const validateYttFields = (params: Record<string, string>): FieldErrors => {
  const fieldErrors: FieldErrors = {};

  if (!params.mainUserName?.trim()) {
    fieldErrors.mainUserName = t.VPVR(fieldCantBeEmptyMsg);
  }

  if (!params.mainUserEmail?.trim()) {
    fieldErrors.mainUserEmail = t.VPVR(fieldCantBeEmptyMsg);
  } else if (!EMAIL_REGEX.test(params.mainUserEmail.trim())) {
    fieldErrors.mainUserEmail = t.DUPA(invalidValueMsg);
  }

  if (params.mainUserPhone?.trim() && !PHONE_NUMBER_REGEX.test(params.mainUserPhone.trim())) {
    fieldErrors.mainUserPhone = t.DUPA(invalidValueMsg);
  }

  return fieldErrors;
};

export const YttAdditionalFields = forwardRef<YttAdditionalFieldsHandle, YttAdditionalFieldsProps>(
  ({ globalIndex }, ref) => {
    const methods = useFormContext<PlaceOrderFormData>();
    const [errors, setErrors] = useState<FieldErrors>({});
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const getAdditionalParameters = (): Record<string, string> => {
      return (
        (methods.getValues(`products.placeOrderItemUnits.${globalIndex}.additionalParameters`) as Record<
          string,
          string
        >) ?? {}
      );
    };

    const setAdditionalParameter = (key: string, value: string) => {
      const current = getAdditionalParameters();
      const updated = { ...current, [key]: value };
      methods.setValue(`products.placeOrderItemUnits.${globalIndex}.additionalParameters`, updated, {
        shouldDirty: true,
        shouldTouch: true,
      });
    };

    const handleValidate = (): boolean => {
      setSubmitAttempted(true);
      const fieldErrors = validateYttFields(getAdditionalParameters());
      setErrors(fieldErrors);
      return Object.keys(fieldErrors).length === 0;
    };

    const handleScrollToFirstError = () => {
      const firstErrorEl = containerRef.current?.querySelector(`.${dsClass.INPUT_ERROR}`);
      if (firstErrorEl) {
        scrollTo(firstErrorEl as HTMLElement);
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        validate: handleValidate,
        scrollToFirstError: handleScrollToFirstError,
        getErrorCount: () => Object.keys(errors).length,
      }),
      [errors] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const params = getAdditionalParameters();

    const handleChange = (key: string, value: string) => {
      setAdditionalParameter(key, value);
      if (submitAttempted) {
        const updated = { ...getAdditionalParameters(), [key]: value };
        setErrors(validateYttFields(updated));
      }
    };

    return (
      <div ref={containerRef} className={dsClass.PADDING_TOP_4}>
        <Input
          label={`${t.ILJ6(mainUserNameMsg)} *`}
          type="text"
          value={params.mainUserName ?? ''}
          onChange={e => handleChange('mainUserName', e.target.value)}
          error={errors.mainUserName}
        />

        <Input
          label={`${t.CI5R(mainUserEmailMsg)} *`}
          type="text"
          value={params.mainUserEmail ?? ''}
          onChange={e => handleChange('mainUserEmail', e.target.value)}
          error={errors.mainUserEmail}
        />

        <Input
          label={t.BMK4(mainUserPhoneMsg)}
          type="tel"
          value={params.mainUserPhone ?? ''}
          onChange={e => handleChange('mainUserPhone', e.target.value)}
          error={errors.mainUserPhone}
        />
      </div>
    );
  }
);

YttAdditionalFields.displayName = 'YttAdditionalFields';
